import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "./context/ThemeContext";
import { useAuth } from "./context/AuthContext";
import Dashboard from "./pages/Dashboard";
import AccountSettings from "./pages/AccountSettings";
import ShippingAddress from "./pages/ShippingAddress";
import Orders from "./pages/Orders";
import AllProducts from "./pages/AllProducts";
import ProductDetail from "./pages/ProductDetail";
import { CartProvider } from "./context/CartContext";
import { Navigate } from "react-router-dom";
import AdminDashboard from "./admin/AdminDashboard";
import RequireAdminRoute from "./admin/RequireAdminRoute";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AgeVerificationModal from "./components/AgeVerificationModel";
import Home from "./pages/Home";
import AboutUs from "./pages/AboutUs";
import Services from "./pages/Services";
import ContactUs from "./pages/ContactUs";
import Register from "./pages/Register";
import Security from "./pages/Security";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Checkout from "./pages/Checkout";
import CartPage from "./pages/CartPage";
import OrderConfirmation from "./pages/OrderConfirmation";
import Support from "./pages/Support";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import Disclaimers from "./pages/Disclaimers";
import FAQ from "./pages/FAQ";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import WholesalePrompt from "./components/WholesalePrompt";
import ChatWidget from "./components/ChatWidget";
import PendingApproval from "./pages/PendingApproval";

const pageVariants = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
};

function AppContent() {
  const { user, userStatus } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  return (
    <>
      <ScrollToTop />
      <AgeVerificationModal />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={theme}
        />
        <WholesalePrompt />
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="flex-1"
          >
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/services" element={<Services />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/account" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
              <Route path="/support" element={<Support />} />
              <Route path="/login" element={
                user && user.emailVerified && userStatus === "approved" ? <Navigate to="/dashboard" replace /> :
                user && user.emailVerified && userStatus !== "approved" ? <Navigate to="/pending-approval" replace /> :
                <Login />
              } />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/register" element={
                user && user.emailVerified && userStatus === "approved" ? <Navigate to="/dashboard" replace /> :
                user && user.emailVerified && userStatus !== "approved" ? <Navigate to="/pending-approval" replace /> :
                <Register />
              } />
              <Route path="/pending-approval" element={<PendingApproval />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/account/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
              <Route path="/account/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
              <Route path="/shipping-address" element={<ProtectedRoute><ShippingAddress /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
              <Route path="/products/details/:productId" element={<ProductDetail />} />
              <Route path="/products/:category" element={<AllProducts />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={user ? <Checkout /> : <Navigate to="/login" />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/shipping" element={<ShippingPolicy />} />
              <Route path="/return" element={<ReturnPolicy />} />
              <Route path="/disclaimer" element={<Disclaimers />} />
              <Route path="/admin" element={<RequireAdminRoute><AdminDashboard /></RequireAdminRoute>} />
            </Routes>
          </motion.div>
        </AnimatePresence>
        <Footer />
      </div>
      <ChatWidget />
    </>
  );
}

function App() {
  return (
    <CartProvider>
      <Router>
        <AppContent />
      </Router>
    </CartProvider>
  );
}


export default App;
